import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createScrollTrigger } from 'util/scroll/scrollTrigger.utils';
import type { TransitionDirection, TransitionController } from 'util/core-transition-component';
import type { ScrollProviderContextType } from 'components/unlisted/ScrollProvider';
import { useScrollProviderContext } from 'components/unlisted/ScrollProvider';
import { findTransitionController } from 'util/core-transition-component';

/**
 *
 * @EXAMPLE 1
 * const elementRef = useRef<HTMLDivElement>(null);
 *
 * // Connect the transitionController to the lifecycle
 * const transitionController = useTransitionController(
 *   () => ({
 *     ref: elementRef,
 *     refs: {
 *       elementRef,
 *     },
 *     setupTransitionInTimeline,
 *   }),
 * );
 *
 * const { inView } = useScrollTrigger({
 *   trigger: elementRef,
 *   transition: {
 *     controller: transitionController,
 *   },
 *   vars:{
 *     onEnter(){
 *       console.log('enter');
 *     },
 *   },
 * });
 *
 * @EXAMPLE 2
 * const elementRef = useRef<HTMLDivElement>(null);
 * const timeline = useRef(gsap.timeline({paused: true}));
 *
 * const { inView } = useScrollTrigger({
 *   trigger: elementRef,
 *   animation: timeline.current,
 *   vars:{
 *     onEnter(){
 *       console.log('enter');
 *     },
 *   },
 * });
 *
 * useEffect(() => {
    console.log(inView);
  }, [inView]);
 *
 */

export type TriggerState = 'onEnter' | 'onLeave' | 'onLeaveBack' | 'onEnterBack';

export type OptionsType = {
  trigger: RefObject<HTMLElement | null>;
  pinSpacer?: RefObject<HTMLElement | null>;
  animation?: gsap.core.Timeline;
  transition?: {
    ref?: RefObject<HTMLElement | null>;
    controller?: TransitionController;
    direction?: TransitionDirection;
  };
  pin?: RefObject<HTMLElement | null>;
  endTrigger?: RefObject<HTMLElement | null>;
  vars?: ScrollTrigger.Vars;
  disabled?: boolean;
  reverseOnLeaveBack?: boolean;
  canTransitionIn?: boolean;
  transitionInDelay?: number;
  enableInView?: boolean;
};

export default function useScrollTrigger(options: OptionsType): {
  inView: boolean | undefined;
} {
  const [inView, setInView] = useState<boolean>();
  const optionsRef = useRef(options);
  const {
    animation,
    trigger,
    pin,
    endTrigger,
    pinSpacer,
    vars,
    transition,
    disabled,
    reverseOnLeaveBack,
    transitionInDelay,
    enableInView,
  } = optionsRef.current;
  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;
  // const [triggerState, setTriggerState] = useState<TriggerState>();
  const timeline = useRef<gsap.core.Timeline>(undefined);
  const triggerState = useRef<TriggerState>(undefined);
  const canTransition = useRef(options.canTransitionIn);

  const setTriggerState = useCallback(
    (state: TriggerState) => {
      triggerState.current = state;

      // Reset the timeline, so it can be re-triggered again when it hits the viewport from a normal enter flow.
      const reTriggerTimeline = !vars?.scrub && !vars?.once && !reverseOnLeaveBack;

      if (triggerState.current === 'onEnter') {
        if (reTriggerTimeline) {
          timeline?.current?.paused(
            !(typeof canTransition.current === 'boolean' ? canTransition.current : true),
          );
        }
      }

      if (triggerState.current === 'onLeaveBack') {
        if (reTriggerTimeline) timeline?.current?.pause(-(options.transitionInDelay || 0), false);
        if (reverseOnLeaveBack) timeline?.current?.reverse();
      }
    },
    [canTransition, options.transitionInDelay, reverseOnLeaveBack, vars?.once, vars?.scrub],
  );

  useEffect(
    () => {
      canTransition.current = options.canTransitionIn;

      if (!vars?.scrub && triggerState.current === 'onEnter') {
        timeline?.current?.paused(
          !(typeof options.canTransitionIn === 'boolean' ? options.canTransitionIn : true),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.canTransitionIn],
  );

  useEffect(
    () => {
      if (!disabled) {
        if (transition?.ref) {
          const transitionController = findTransitionController(transition.ref);
          if (transitionController) {
            timeline.current = transitionController?.getTimeline(transition?.direction || 'in');
          }
        }

        if (!timeline.current) {
          timeline.current =
            transition?.controller?.getTimeline(transition?.direction || 'in') ?? animation;
        }

        if (timeline.current && typeof transitionInDelay === 'number') {
          timeline.current?.delay(transitionInDelay);
        }

        let pinType: 'fixed' | 'transform' | undefined;
        if (vars?.pinType) {
          pinType = scrollContext?.hasSmoothScroll?.current ? 'transform' : 'fixed';
        }

        // todo: test force 'catch-up' scrub of 0.5s
        // const scrub = vars?.scrub === true ? 0.7 : vars?.scrub;

        const scrollTriggerVars = {
          scroller: scrollContext?.hasSmoothScroll?.current
            ? scrollContext?.scrollWrapperRef?.current
            : undefined,
          pin: pin?.current,
          pinSpacer: pinSpacer?.current,
          endTrigger: endTrigger?.current,
          ...vars,
          // scrub,
          pinType,
          // preventOverlaps: true,
        };

        scrollTriggerVars.onEnter = (data) => {
          if (enableInView) {
            setInView(true);
          }
          setTriggerState('onEnter');
          if (vars?.onEnter) vars?.onEnter(data);
        };

        scrollTriggerVars.onLeave = (data) => {
          if (enableInView) {
            setInView(false);
          }
          if (vars?.onLeave) vars?.onLeave(data);
        };

        scrollTriggerVars.onEnterBack = (data) => {
          if (enableInView) {
            setInView(true);
          }
          if (vars?.onEnterBack) vars?.onEnterBack(data);
        };

        scrollTriggerVars.onLeaveBack = (data) => {
          if (enableInView) {
            setInView(false);
          }
          setTriggerState('onLeaveBack');
          if (vars?.onLeaveBack) vars?.onLeaveBack(data);
        };

        const scrollTrigger = createScrollTrigger(
          trigger.current as HTMLElement,
          timeline.current,
          scrollTriggerVars,
        );

        return () => {
          scrollTrigger?.kill();
        };
      }

      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // animation,
      // trigger,
      // endTrigger,
      // pin,
      // transition,
      // vars,
      // scrollContext?.scrollWrapperRef,
      // scrollContext?.hasSmoothScroll,
      // disabled,
      // setTriggerState,
      // transitionInDelay,
    ],
  );

  return {
    inView,
  };
}
